import { ColumnProps } from 'antd/lib/table'
import { CheckboxValueType } from 'antd/lib/checkbox/Group'
import { UrlFiltersType, TableViewPreferencesType } from '@OpsWb/types'
import { MPTabsType } from '@MfgPartner/types'
import styles from '@OpsQuality/styles.css'
import { hqUploadStatus } from '@OpsQuality/logic'
import moment from 'moment-timezone'
import { DEFAULT_BUSINESS_UNIT } from '@OpsWb/constants'

import Text from '~/core/Text'
import Link from '~/core/Link'
import {
    OPS_PRODUCTION_PATH,
    OPS_QUALITY_PATH,
} from '~/redux-modules/preferences'
import Box from '~/core/Box'
import { JOB_REQUEST_STATUS_FOR_LIST } from '../../constants'

import { getAdminJobRequestsQuality_adminJobRequests_items as JobRequestType } from './__generated__/getAdminJobRequestsQuality'
import { DATE_FORMAT } from './logic'
import {
    InspectionStatusTag,
    Payout,
    Priority,
    ProcessNames,
    ProjectName,
} from './TableColumns'

type JobsRequestListTableColumnPropsType = {
    userPreferences?: TableViewPreferencesType
    appliedFilters: UrlFiltersType
    onFilter?: (
        filterType: string,
        filterValue: CheckboxValueType[],
        confirm?: () => void,
        route?: string,
        removeOthers?: boolean,
        nextTab?: MPTabsType,
    ) => void
}

export const getJobsRequestListTableColumns = (
    props: JobsRequestListTableColumnPropsType,
) => {
    const { userPreferences } = props

    const columns: ColumnProps<JobRequestType>[] = [
        {
            title: 'Job request name',
            dataIndex: 'name',
            fixed: 'left' as const,
            width: userPreferences?.['jobName']?.width ?? 240,
            ellipsis: true,
            render: (text: string, record: JobRequestType) => (
                <>
                    <Link
                        name="job-request-link"
                        placement="job-request-list-table"
                        to={`${OPS_QUALITY_PATH}/${record.id}/`}
                    >
                        {text}
                    </Link>
                    {record.rfqNumber && (
                        <Box direction="row" display="flex" alignItems="center">
                            <Text className={styles.rfqTextStyle}>
                                RFQ#{record.rfqNumber}
                            </Text>
                        </Box>
                    )}
                </>
            ),
        },
        {
            title: 'Sales order name',
            dataIndex: 'salesOrderName',
            key: 'salesorder',
            width: userPreferences?.['salesorder']?.width ?? 155,
            render: (text: string, record: JobRequestType) => (
                <Link
                    name="sales-order-link"
                    data-test-target="sale-order-name-link"
                    placement="job-request-list-table"
                    target="_blank"
                    href={`${OPS_PRODUCTION_PATH}/sales-order-details/${record.salesOrderName}`}
                >
                    {text}
                </Link>
            ),
        },
        {
            title: 'Business unit',
            dataIndex: 'businessUnit',
            key: 'businessUnit',
            width: userPreferences?.['businessUnit']?.width ?? 155,
            render: (text: string, record: JobRequestType) => (
                <Text>{record.businessUnit ?? DEFAULT_BUSINESS_UNIT}</Text>
            ),
        },
        {
            title: 'Project',
            dataIndex: 'platformProjectName',
            key: 'platformProjectName',
            render: (value: string, record: JobRequestType) => (
                <ProjectName
                    isSecurePlus={record?.project?.isSecurePlus ?? false}
                    platformProjectName={record?.project?.name ?? ''}
                />
            ),
            width: userPreferences?.['platformProjectName']?.width ?? 150,
        },
        {
            title: 'MP due date',
            dataIndex: 'vendorDueDate',
            key: 'vendorDueDate',
            render: (date: Date) => moment(date).format(DATE_FORMAT),
            sorter: true,
            width: userPreferences?.['vendorDueDate']?.width ?? 215,
        },
        {
            title: 'Priority',
            dataIndex: 'priority',
            key: 'priority',
            render: (_value: string, record: JobRequestType) => (
                <Priority jobRequest={record} />
            ),
            width: userPreferences?.['priority']?.width ?? 145,
        },
        {
            title: 'Job request status',
            dataIndex: 'status',
            key: 'status',
            render: (value: keyof typeof JOB_REQUEST_STATUS_FOR_LIST) => {
                return JOB_REQUEST_STATUS_FOR_LIST[value]
            },
            width: userPreferences?.['status']?.width ?? 155,
        },
        {
            title: 'Processes',
            dataIndex: 'process',
            key: 'platformProcessTypeId',
            render: (_value: string, record: JobRequestType) => (
                <ProcessNames jobRequest={record} />
            ),
            width: userPreferences?.['platformProcessTypeId']?.width ?? 135,
        },
        {
            title: 'MP name',
            render: (value: string, record: JobRequestType) => {
                return record?.mfgPartner?.displayName
            },

            key: 'company',
            width: userPreferences?.['company']?.width ?? 215,
        },
        {
            title: 'Payout',
            dataIndex: 'payout',
            key: 'payout',
            render: (value: number) => <Payout totalAmount={value} />,
            width: userPreferences?.['payout']?.width ?? 130,
        },
        {
            title: 'MFG DRI',
            render: (value: string, record: JobRequestType) => {
                return record?.mfgDri?.displayName
            },
            key: 'mfgDri',
            width: userPreferences?.['mfgDri']?.width ?? 155,
        },

        {
            title: 'Inspection status',
            dataIndex: 'inspectionStatus',
            key: 'inspectionStatus',
            render: value => <InspectionStatusTag reportState={value} />,
            width: userPreferences?.['inspectionStatus']?.width ?? 155,
        },
        {
            title: 'HQ upload',
            dataIndex: 'hqUploadStatus',
            key: 'hqUploadStatus',
            render: (_value: string, record: JobRequestType) => {
                return record && hqUploadStatus(record.jobRequestAttachments)
            },
            width: userPreferences?.['hqUploadStatus']?.width ?? 155,
        },
    ]

    return columns
}
